import { faAngleLeft, faAngleRight } from "@fortawesome/pro-solid-svg-icons";
import { computed, signal } from "@preact/signals-react";
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { fieldPropertyMap } from "../../Fields/PropertyTypes/FieldPropertyMap";
import math from "../../Mathjs/CustomMathjsInstance";

//#region Signals for ColumnsConstructorWindow
export const selectedColumnId = signal();
export const columnsConstructorSessionData = signal([]);
export const propertiesVisible = signal(false);

export const columnsConstructorValidationErrors = computed(() => {
  return columnsConstructorSessionData.value
    .filter((row) => {
      if (!row.columnType) return true;

      const { isValid } = validateColumnName(row.columnName || "", row.id);
      return !isValid;
    })
    .map((row) => row.id);
});

export const validateColumnName = (value, currentId = null) => {
  if (!value) {
    return { isValid: false, errorMessage: "Column Name is required." };
  }

  // Get all column names except the current one being edited
  const otherColumnNames = columnsConstructorSessionData.value
    .filter(({ id }) => id !== currentId)
    .map(({ columnName }) => columnName)
    .filter((name) => name);

  if (otherColumnNames.some((name) => name.trim().toLowerCase() === value.trim().toLowerCase())) {
    return { isValid: false, errorMessage: "Column Name must be unique." };
  }

  const firstChar = value.charAt(0);

  if (/\d/.test(firstChar)) {
    return { isValid: false, errorMessage: "Column Name cannot start with numbers." };
  }

  if (/[!@#$%^&*()_+\-=\]{};':"\\|,.<>?]/.test(firstChar)) {
    return { isValid: false, errorMessage: "Column Name cannot start with special symbols." };
  }

  if (Object.hasOwn(math, value)) {
    return {
      isValid: false,
      errorMessage: `Column Name "${value}" conflicts with an existing math function.`,
    };
  }

  return { isValid: true, errorMessage: "" };
};

export const columnsConstructorUniqueColumnNames = computed(() => {
  const nameCounts = {};

  // Count occurrences of each column name (case-insensitive)
  columnsConstructorSessionData.value.forEach(({ columnName }) => {
    if (!columnName) return;

    const normalizedName = columnName.trim().toLowerCase();
    if (!nameCounts[normalizedName]) {
      nameCounts[normalizedName] = [];
    }
    nameCounts[normalizedName].push(columnName);
  });

  // Return original names that appear more than once (case-insensitive)
  return Object.values(nameCounts)
    .filter((names) => names.length > 1)
    .flat();
});

export const maxColumnId = computed(() =>
  Math.max(
    ...(columnsConstructorSessionData.value.length > 0
      ? columnsConstructorSessionData.value.map((item) => item.id)
      : [-1])
  )
);
export const selectedColumnIndex = computed(() => {
  return columnsConstructorSessionData.value.findIndex(({ id }) => id === selectedColumnId.value);
});

export const gridPaneSize = signal("70%");
export const nestedPanes = computed(() => {
  return [
    {
      size: !propertiesVisible.value ? "95%" : gridPaneSize.value,
    },
    { resizable: propertiesVisible.value },
    {
      size: !propertiesVisible.value ? "5%" : "30%",
    },
  ];
});

export const editedPropertyValues = signal({});
export const fieldProperties = computed(() => {
  if (selectedColumnIndex.value < 0) return [];
  if (!fieldPropertyMap) return [];
  return fieldPropertyMap[
    columnsConstructorSessionData.value[selectedColumnIndex].columnType
  ]?.table?.map((property) => ({
    ...property,
    hidden: !propertiesVisible.value,
  }));
});
export const selectedColumnFieldPropertyValues = computed(() => {
  const index = columnsConstructorSessionData.value?.findIndex(
    ({ id }) => id === selectedColumnId.value
  );
  return columnsConstructorSessionData.value?.[index]?.properties || {};
});

export const items = computed(() => {
  return [
    {
      id: 0,
      renderer: function () {
        return (
          <div className="align-columns">
            <ColorChangingIcon
              icon={propertiesVisible.value ? faAngleRight : faAngleLeft}
              onClick={() => (propertiesVisible.value = !propertiesVisible.value)}
              initialPrimaryColor="#0078d4"
              initialSecondaryColor="#0078d4"
              initialSize={32}
              hoverSize={32}
              margin="10px 0 0 15px"
            />
            {propertiesVisible.value && (
              <h3 style={{ margin: "5px 0 0 0" }}>
                {columnsConstructorSessionData.value[selectedColumnIndex.value]?.columnName}{" "}
                Properties
              </h3>
            )}
          </div>
        );
      },
    },
  ].map((item) => ({ ...item }));
});

export const propertiesWithHeaders = computed(() => {
  const groupByCategory = (properties) => {
    if (!Array.isArray(properties)) return {};

    return properties?.reduce((acc, prop) => {
      if (!acc[prop.propertyCategory]) {
        acc[prop.propertyCategory] = [];
      }
      acc[prop.propertyCategory].push(prop);
      return acc;
    }, {});
  };

  const categorizedProperties = groupByCategory(fieldProperties.value);

  return Object.entries(categorizedProperties).flatMap(([category, props]) => [
    {
      isHeader: "true",
      category,
      renderer: function () {
        return (
          <div className="property-category">
            <h3>{category}</h3>
          </div>
        );
      },
    },
    ...props,
  ]);
});
//#endregion Signals for ColumnsConstructorWindow
